import { ChangeEvent, Dispatch, SetStateAction } from "react";

import { useTranslation } from "react-i18next";

import moment from "moment-timezone";
import { ApiResources } from "@/api/resources";
import Checkbox from "@/components/CheckBox/Checkbox";
import SingleDayPicker from "@/components/DatePickers/DayPickers/SingleDayPicker/SingleDayPicker";
import InputField from "@/components/InputField/InputField";
import QueryDropdown from "@/components/QueryDropdown/QueryDropdown";
import {
  API_KEY_FIELD_NAMES,
  ApiKeyErrors,
  ApiKeyObjectState
} from "@/Pages/Integrations/types";
import {
  API_KEY_INPUT_LENGTH,
  DATE_FORMATS,
  ID_TYPE,
  inputFieldMarginBottom
} from "@/shared";

interface ApiKeyDialogContentProps {
  isLoading: boolean;
  apiKeyObject: ApiKeyObjectState;
  errors: ApiKeyErrors;
  hasUpdatedEnable: boolean;
  setErrors: Dispatch<SetStateAction<ApiKeyErrors>>;
  setHasUpdatedEnable: Dispatch<SetStateAction<boolean>>;
  setApiKeyObject: Dispatch<SetStateAction<ApiKeyObjectState>>;
  readOnlyCompanyField?: boolean;
}

const ApiKeyDialogContent = ({
  isLoading,
  apiKeyObject,
  errors,
  hasUpdatedEnable,
  setHasUpdatedEnable,
  setErrors,
  setApiKeyObject,
  readOnlyCompanyField
}: ApiKeyDialogContentProps) => {
  const { t } = useTranslation();

  const validateName = (value: string): string => {
    const trimmed = value.trim();

    if (!trimmed) {
      return t("Dialog##api key name required");
    }

    if (trimmed.length > API_KEY_INPUT_LENGTH.Name) {
      return t("Dialog##api key name too long", {
        maxNumber: API_KEY_INPUT_LENGTH.Name
      });
    }

    return "";
  };

  const handleNameChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setApiKeyObject(prev => ({
      ...prev,
      [API_KEY_FIELD_NAMES.Name]: event.target.value
    }));

    if (errors[API_KEY_FIELD_NAMES.Name]) {
      setErrors(prev => ({ ...prev, [API_KEY_FIELD_NAMES.Name]: "" }));
    }
  };

  const handleNameBlur = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const errorMessage = validateName(event.target.value);
    if (errorMessage) {
      setErrors(prev => ({
        ...prev,
        [API_KEY_FIELD_NAMES.Name]: errorMessage
      }));
    }
  };

  const handleCompanyChange = (value: any) => {
    setApiKeyObject(prev => ({
      ...prev,
      [API_KEY_FIELD_NAMES.SelectedCompany]: value
    }));
  };

  const handleDateChange = (value: Date | null) => {
    const formattedDate = value
      ? moment(value).format(DATE_FORMATS.YearMonthDay)
      : null;

    setApiKeyObject(prev => ({
      ...prev,
      [API_KEY_FIELD_NAMES.ExpirationDate]: formattedDate
    }));
  };

  const handleChangeDeactivate = () => {
    !hasUpdatedEnable && setHasUpdatedEnable(true);

    setApiKeyObject(prev => {
      return {
        ...prev,
        [API_KEY_FIELD_NAMES.Enabled]: !prev.enabled
      };
    });
  };

  const maxExpirationDate = moment().add(1, "year");
  const externalDate = apiKeyObject.expirationDate
    ? new Date(apiKeyObject.expirationDate)
    : null;

  return (
    <div>
      <InputField
        data-testid="api-key-name-field"
        size="medium"
        fullWidth
        required
        value={apiKeyObject[API_KEY_FIELD_NAMES.Name]}
        labelLeft={t("Dialog##api key name")}
        placeholder={t("Dialog##introduce name")}
        onChange={handleNameChange}
        onBlur={handleNameBlur}
        errorText={errors[API_KEY_FIELD_NAMES.Name]}
        disabled={isLoading}
        customStyle={inputFieldMarginBottom}
      />

      <QueryDropdown
        testId="api-key-company-dropdown"
        disableClearable
        resource={ApiResources.CompanyList}
        idType={ID_TYPE.Id}
        value={apiKeyObject[API_KEY_FIELD_NAMES.SelectedCompany]}
        size="medium"
        fullWidth
        isRequired
        change={(_, val) => handleCompanyChange(val)}
        labelLeft={t("Dialog##company")}
        textFieldParams={{ placeholder: t("Dialog##select") }}
        isDisabled={isLoading}
        readOnly={readOnlyCompanyField}
        customStyle={inputFieldMarginBottom}
      />

      <SingleDayPicker
        label={t("Dialog##expiration date optional")}
        minDate={moment().add(1, "day")}
        externalValue={externalDate}
        maxDate={maxExpirationDate}
        onChange={handleDateChange}
        disabled={isLoading || readOnlyCompanyField}
      />

      <Checkbox
        id="api-key-deactivate-checkbox"
        infoIconTooltip={t(
          "Dialog##integrations##deactivate integration tooltip"
        )}
        infoIconTooltipSize="medium"
        checked={!apiKeyObject.enabled}
        label={t("Dialog##integrations##deactivate api key")}
        onChange={handleChangeDeactivate}
        disabled={isLoading}
        customStyle={{
          marginTop: "16px"
        }}
      />
    </div>
  );
};

export default ApiKeyDialogContent;
